<mat-expansion-panel
  class="!overflow-hidden !rounded-2xl !border !border-gray-6 !bg-surface-default !shadow-sm transition-all duration-200 hover:!border-gray-7 !before:hidden relative"
  [expanded]="expanded()"
  (expandedChange)="expanded.set($event)"
>
  <!-- Subtle decorative ambient glow -->
  <div
    class="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-purple-a2 blur-3xl"
  ></div>

  <!-- Card Header -->
  <mat-expansion-panel-header
    class="!h-auto !py-4.5 !px-6 hover:!bg-gray-2/40 transition-colors [&_.mat-content]:!flex-row [&_.mat-content]:!items-center [&_.mat-content]:!justify-between [&_.mat-content]:!w-full [&_.mat-content]:!m-0 [&_.mat-content]:!overflow-visible"
  >
    <div class="flex items-center justify-between w-full gap-4 mr-3">
      <div class="flex items-center gap-3.5 min-w-0">
        <div
          class="h-11 w-11 flex items-center justify-center rounded-xl bg-purple-a2 text-purple-11 border border-purple-a4 shadow-xs shrink-0"
        >
          <mat-icon class="text-2xl leading-none">analytics</mat-icon>
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h4 class="text-base sm:text-lg font-bold text-gray-12 m-0">Profil socio-économique INSEE</h4>
          </div>
          <p class="text-xs sm:text-sm text-gray-11 m-0 mt-0.5 truncate hidden sm:block">
            Revenus des ménages et niveau de vie moyen à l'échelle du quartier.
          </p>
        </div>
      </div>

      <div class="shrink-0">
        @if (affluenceInfo(); as affluence) {
          <div
            class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3.5 py-1.5 rounded-full border shadow-xs"
            [class]="affluence.classes"
          >
            <mat-icon fontIcon="trending_up" class="text-sm leading-none shrink-0"></mat-icon>
            <span>{{ affluence.label }}</span>
          </div>
        } @else {
          <div
            class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3 py-1 rounded-full border border-gray-6 bg-gray-2 text-gray-11 shadow-2xs"
          >
            <mat-icon fontIcon="hourglass_empty" class="text-xs leading-none shrink-0"></mat-icon>
            <span>Calcul en cours</span>
          </div>
        }
      </div>
    </div>
  </mat-expansion-panel-header>

  <div class="px-6 pb-6 pt-3 border-t border-gray-5">
    @if (insee(); as data) {
      <!-- Main Content -->
      <div class="relative mt-5 flex flex-col gap-5">
        <!-- 4-Tier Affluence Visual Ladder -->
        <div class="rounded-xl border border-gray-5 bg-gray-2/70 p-4 sm:p-5">
          <div class="flex items-center justify-between text-sm text-gray-11 mb-2.5 font-medium">
            <span class="font-semibold text-gray-12">Positionnement socio-économique du secteur</span>
            <span class="text-xs text-gray-10">Échelle nationale</span>
          </div>

          <div class="grid grid-cols-4 gap-2">
            @for (
              step of [
                {
                  label: 'Modeste',
                  desc: 'Inférieur à la médiane',
                  activeColor: 'bg-blue-9 text-white',
                  border: 'border-blue-a4',
                  text: 'text-blue-11',
                },
                {
                  label: 'Médian',
                  desc: 'Standard national',
                  activeColor: 'bg-indigo-9 text-white',
                  border: 'border-indigo-a4',
                  text: 'text-indigo-11',
                },
                {
                  label: 'Aisé',
                  desc: 'Supérieur à la moyenne',
                  activeColor: 'bg-jade-9 text-white',
                  border: 'border-jade-a4',
                  text: 'text-jade-11',
                },
                {
                  label: 'Très aisé',
                  desc: 'Top 10 % national',
                  activeColor: 'bg-purple-9 text-white',
                  border: 'border-purple-a4',
                  text: 'text-purple-11',
                },
              ];
              track step.label;
              let i = $index
            ) {
              <div
                class="relative flex flex-col items-center justify-center rounded-lg p-3 text-center transition-all duration-300 border"
                [class]="
                  i === affluenceStepIndex()
                    ? 'bg-surface-default shadow-xs border-2 ' + step.border
                    : 'bg-gray-3/60 border-transparent opacity-65'
                "
              >
                <span
                  class="text-xs sm:text-sm font-bold"
                  [class]="i === affluenceStepIndex() ? step.text : 'text-gray-11'"
                >
                  {{ step.label }}
                </span>
                <span class="hidden sm:inline-block text-xs text-gray-10 mt-0.5">{{ step.desc }}</span>
                @if (i === affluenceStepIndex()) {
                  <span
                    class="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-12 text-[10px] text-white font-bold"
                    >✓</span
                  >
                }
              </div>
            }
          </div>
        </div>

        <!-- 3 Metrics Grid -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <!-- 1. Median Income Hero Card -->
          <div
            class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs"
          >
            <div>
              <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
                <span>Revenu fiscal médian</span>
                <mat-icon
                  fontIcon="account_balance_wallet"
                  class="text-sm leading-none text-gray-9"
                ></mat-icon>
              </div>
              <div class="mt-2 flex items-baseline gap-1.5">
                @if (data.medianIncome) {
                  <span class="text-2xl sm:text-3xl font-black tracking-tight text-gray-12">
                    {{ data.medianIncome | currency: 'EUR' : 'symbol' : '1.0-0' : 'fr-FR' }}
                  </span>
                  <span class="text-sm font-bold text-gray-11">/ an</span>
                } @else {
                  <span class="text-base font-semibold text-gray-11">Non renseigné</span>
                }
              </div>
            </div>
            <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
              {{ affluenceInfo()?.description || 'Revenu disponible par unité de consommation (foyer).' }}
            </p>
          </div>

          <!-- 2. City Comparison Card -->
          <div
            class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs"
          >
            <div>
              <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
                <span>Comparaison communale</span>
                <mat-icon fontIcon="location_city" class="text-sm leading-none text-gray-9"></mat-icon>
              </div>
              <div class="mt-2 flex items-center gap-2">
                @if (cityComparison(); as comp) {
                  <div
                    class="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm sm:text-base font-bold border {{
                      comp.bgClass
                    }} {{ comp.colorClass }}"
                  >
                    <mat-icon [fontIcon]="comp.icon" class="text-base sm:text-lg leading-none"></mat-icon>
                    <span>{{ comp.isPositive ? '+' : '' }}{{ comp.percent }} %</span>
                  </div>
                  @if (cityName()) {
                    <span class="text-xs sm:text-sm text-gray-11 font-medium truncate"
                      >vs {{ cityName() }}</span
                    >
                  }
                } @else {
                  <span class="text-base font-semibold text-gray-11">Donnée non calculée</span>
                }
              </div>
            </div>
            <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
              @if (data.communeMedianIncome) {
                Revenu communal de référence :
                <span class="font-semibold text-gray-12">{{
                  data.communeMedianIncome | currency: 'EUR' : 'symbol' : '1.0-0' : 'fr-FR'
                }}</span>
              } @else {
                Comparaison par rapport à la moyenne globale de la commune.
              }
            </p>
          </div>

          <!-- 3. Department Comparison Card -->
          <div
            class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs"
          >
            <div>
              <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
                <span>Comparaison départementale</span>
                <mat-icon fontIcon="domain" class="text-sm leading-none text-gray-9"></mat-icon>
              </div>
              <div class="mt-2 flex items-center gap-2">
                @if (departmentComparison(); as deptComp) {
                  <div
                    class="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm sm:text-base font-bold border {{
                      deptComp.bgClass
                    }} {{ deptComp.colorClass }}"
                  >
                    <mat-icon [fontIcon]="deptComp.icon" class="text-base sm:text-lg leading-none"></mat-icon>
                    <span>{{ deptComp.isPositive ? '+' : '' }}{{ deptComp.percent }} %</span>
                  </div>
                } @else {
                  <span class="text-base font-semibold text-gray-11">Moyenne départementale</span>
                }
              </div>
            </div>
            <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
              Différentiel de richesse fiscale calculé par rapport aux ménages du département.
            </p>
          </div>
        </div>
      </div>

      <!-- Micro footer note -->
      <div class="mt-4 flex items-center gap-2 text-xs text-gray-11 pt-2">
        <mat-icon fontIcon="info_outline" class="text-sm text-purple-11 leading-none shrink-0"></mat-icon>
        <span
          >Dispositif Filosofi INSEE (Fichier localisé social et fiscal) géolocalisé par mailles de 200
          m.</span
        >
      </div>
    } @else {
      <!-- Empty / Pending State -->
      <div
        class="mt-4 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-6 py-8 text-center bg-gray-2/50"
      >
        <div class="h-10 w-10 rounded-full bg-gray-3 flex items-center justify-center text-gray-10">
          <mat-icon class="text-xl">sync_disabled</mat-icon>
        </div>
        <div>
          <p class="text-sm font-semibold text-gray-12 m-0">Indicateurs INSEE en cours de calcul</p>
          <p class="text-sm text-gray-11 m-0 mt-1 max-w-sm">
            Le profil de revenus et l'affluence du secteur sont en cours d'extraction depuis les bases
            Filosofi de l'INSEE.
          </p>
        </div>
      </div>
    }
  </div>
</mat-expansion-panel>
